export const SLIDE_COUNT_STOPS = [2, 3, 5, 10] as const;
export type SlideCountStop = (typeof SLIDE_COUNT_STOPS)[number];

export function isSlideCountStop(value: number): value is SlideCountStop {
	// `readonly [2, 3, 5, 10]`'s `.includes` only accepts `2 | 3 | 5 | 10` —
	// widen to `readonly number[]` so we can test an arbitrary number.
	return (SLIDE_COUNT_STOPS as readonly number[]).includes(value);
}

/**
 * Snap an arbitrary finite number to the nearest allowed slide-count stop.
 * On ties (e.g. 4 is equidistant from 3 and 5), rounds up to the higher
 * stop — users generally prefer "at least as many slides as I asked for".
 *
 * Values outside `[SLIDE_COUNT_STOPS[0], …last]` clamp to the boundary
 * naturally because the distance to the boundary is smallest. NaN /
 * Infinity should be filtered by the caller (e.g. `z.number().finite()`).
 */
export function nearestSlideCountStop(value: number): SlideCountStop {
	let best: SlideCountStop = SLIDE_COUNT_STOPS[0];
	let bestDistance = Math.abs(value - best);
	for (const stop of SLIDE_COUNT_STOPS) {
		const distance = Math.abs(value - stop);
		// `<=` so a tie at equal distance prefers the later (higher) stop,
		// since the array is sorted ascending.
		if (distance <= bestDistance) {
			best = stop;
			bestDistance = distance;
		}
	}
	return best;
}
